<template>
  <div class="app-layout" :class="{ 'sai-open': isSaiModalOpen }">
    <!-- 세션 만료 확인 다이얼로그 -->
    <UiConfirm
      v-model="isSessionExpiredVisible"
      title="세션 만료"
      message="세션이 만료되었습니다. 로그인 페이지로 이동합니다."
      :show-cancel-button="false"
      :close-on-backdrop-click="false"
      confirm-button-text="확인"
      @confirm="handleSessionExpiredConfirm"
    />

    <!-- 로그인 페이지에서는 헤더와 사이드바 숨김 -->
    <TheHeader v-if="!isLoginPage" />
    <div class="content-wrapper">
      <TheSidebar v-if="!isLoginPage && !isPasswordChangePage" @toggle-expanded="toggleExpanded" />
      <main
        ref="mainContent"
        class="main-content"
        :class="{
          expanded: isExpanded,
          'sai-open': isSaiModalOpen,
          'overflow-hidden': shouldHideOverflow,
          'page-transition': isTransitioning,
          'asis-mode': shouldRenderAsis,
          'login-page': isLoginPage,
          'no-sidebar': isPasswordChangePage,
          'sticky-scroll': isHrEvalSettingsPage,
          'kpi-main-min-wide': isKpiPerformancePage,
          'task-board-scroll': isTaskBoardPage && !shouldRenderAsis,
        }"
      >
        <!-- target이 ASIS(레거시)이면 AsisRenderer 렌더링 -->
        <AsisRenderer v-if="shouldRenderAsis" :key="iframeKey" :pgm-id="currentPgmId" :meta="metaToken" />

        <!-- 기본 페이지 렌더링 -->
        <div v-else :key="pageKey">
          <slot />
        </div>
      </main>

      <!-- 스크롤 탑 버튼 (로그인 페이지 제외, window 스크롤 감지) -->
      <ScrollTopButton
        v-if="!isLoginPage && !shouldRenderAsis"
        :threshold="200"
      />
    </div>
  </div>
</template>

<script setup>
import TheHeader from '~/components/common/Layout/TheHeader.vue';
import TheSidebar from '~/components/common/Layout/TheSidebar.vue';
import AsisRenderer from '~/components/common/AsisRenderer.vue';
import { ref, provide, inject, onMounted, onUnmounted, computed, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import AOS from 'aos';
// import { useWebSocketStore } from '~/stores/useWebSocketStore'; // WebSocket 상시 연결 임시 비활성화
import { normalizeMenuRoutePath } from '~/config/tabMenuConfig';
import { useSessionExpiredStore } from '~/stores/useSessionExpiredStore';
import { useAuthStore } from '~/stores/useAuthStore';
import { startSessionMonitor, stopSessionMonitor } from '~/composables/useSessionMonitor';
import { startSessionTimeout, stopSessionTimeout } from '~/composables/useSessionTimeout';

// 현재 라우터 가져오기
const route = useRoute();
const router = useRouter();
const menuStore = useMenuStore();

// HR 평가 설정 페이지: .main-content overflow 제거 → 창 스크롤로 sticky 동작
const isHrEvalSettingsPage = computed(() =>
  /\/hr-evaluation\/conf\/settings(?:\/|$)/.test(route.path)
);

// KPI 실적 관리: 2단(구성원 패널+테이블) 레이아웃 최소 너비 (전역 SCSS :has()보다 scoped .main-content가 이기므로 레이아웃에서 클래스로 적용)
const isKpiPerformancePage = computed(() =>
  /^\/(?:screens\/)?performance\/kpi(?:\/|$)/.test(route.path),
);
const isTaskBoardPage = computed(() =>
  /^\/(?:screens\/)?task\/collaboration\/?$/.test(route.path),
);
const menuSections = computed(() => menuStore.sections);
const isIframePath = path => menuStore.isIframePath(path);

/**
 * 현재 경로에 해당하는 메뉴 아이템 찾기
 * @returns 메뉴 아이템 또는 null
 */
const getCurrentMenuItem = () => {
  const currentPath = normalizeMenuRoutePath(route.path);

  for (const section of menuSections.value) {
    // Direct items 확인
    if (section.items) {
      const item = section.items.find(item => normalizeMenuRoutePath(item.path) === currentPath);
      if (item) return item;
    }

    // Submenus 확인
    if (section.submenus) {
      for (const submenu of section.submenus) {
        if (submenu.items) {
          // 메인 경로 확인
          const item = submenu.items.find(item => normalizeMenuRoutePath(item.path) === currentPath);
          if (item) return item;

          // 탭 경로 확인
          for (const menuItem of submenu.items) {
            if (menuItem.tabs) {
              const tab = menuItem.tabs.find(t => normalizeMenuRoutePath(t.path) === currentPath);
              if (tab) return menuItem;
            }
          }
        }
      }
    }
  }

  return null;
};

// meta 토큰 임시 저장
const metaToken = ref(null);

// 로그인 페이지 여부 확인
const isLoginPage = computed(() => {
  const loginPaths = ['/login', '/login.do'];
  return loginPaths.includes(route.path);
});

// 비밀번호 변경 페이지 여부 확인 (사이드바 숨김용)
// - 강제 비밀번호 변경 (pwInitYn === 'Y' 또는 alertPwChangeYn === 'Y')일 때만 사이드바 숨김
// - 메뉴에서 직접 접근한 경우는 사이드바 표시
const authStore = useAuthStore();
const isPasswordChangePage = computed(() => {
  const passwordChangePaths = [
    '/common/config/password-change/password-change',
    '/screens/common/config/password-change/password-change', // alias
  ];
  const isPasswordPath = passwordChangePaths.includes(route.path);

  // 경로가 비밀번호 변경 페이지이고, 강제 변경이 필요한 경우에만 사이드바 숨김
  if (isPasswordPath) {
    const user = authStore.user;
    const isForceChange = user?.pwInitYn === 'Y' || user?.alertPwChangeYn === 'Y';
    return isForceChange;
  }

  return false;
});

// iframe 컴포넌트 강제 재생성을 위한 키
const iframeKey = ref(0);
// 일반 페이지 컴포넌트 강제 재생성을 위한 키
const pageKey = ref(0);

// contentReload 이벤트 감지하여 해당 탭 내용만 새로고침
onMounted(() => {
  const handleContentReload = event => {
    // ★ ASIS 모드에서는 postMessage로 탭 전환하므로 iframe 리마운트 안 함
    if (shouldRenderAsis.value) {
      console.log('[default.vue] contentReload - ASIS 모드: postMessage로 처리 (iframeKey 증가 안 함)');
      // AsisRenderer에서 tabChanged 이벤트로 postMessage 처리
      return;
    }

    // 일반 페이지(TOBE)에서만 리렌더링
    console.log('[default.vue] contentReload - TOBE 모드: pageKey 증가');
    pageKey.value++;
  };

  window.addEventListener('contentReload', handleContentReload);

  onUnmounted(() => {
    window.removeEventListener('contentReload', handleContentReload);
  });
});

// 현재 경로가 ASIS(레거시)로 렌더링되어야 하는지 확인
const config = useRuntimeConfig();
const isDev = import.meta.dev;
const shouldRenderAsis = computed(() => {
  // 개발환경(3000번)에서는 항상 tobe(Vue 페이지)로 강제
  if (isDev) {
    return false;
  }

  // 메뉴 아이템의 viewType 확인 - API에서 넘어온 데이터 우선
  // 'A' = ASIS (레거시 iframe), 'T' = TOBE (Vue 페이지)
  const menuItem = getCurrentMenuItem();
  if (menuItem && menuItem.viewType) {
    return menuItem.viewType === 'A';
  }

  // 메뉴 아이템이 없거나 viewType이 없는 경우
  // 빌드 시 ASIS 비활성화 옵션이 켜져 있으면 항상 false (하위 호환성)
  if (config.public.disableIframe) {
    return false;
  }

  // 메뉴 로딩 전에 iframe으로 내리면 pgmId가 없어 빈 흰 프레임만 남는다.
  // 슬롯을 유지했다가, 메뉴가 준비된 뒤 viewType으로 다시 판단한다.
  if (!menuSections.value.length) {
    return false;
  }

  // 기존 블랙리스트 방식 사용 (하위 호환성)
  const currentPath = route.path;
  return isIframePath(currentPath);
});

// 현재 경로의 pgmId와 메뉴 타이틀 찾기
const currentPgmId = computed(() => {
  const currentPath = route.path;

  for (const section of menuSections.value) {
    if (section.submenus) {
      for (const submenu of section.submenus) {
        // 1. 메인 경로와 일치하는지 확인
        const item = submenu.items?.find(item => item.path === currentPath);
        if (item?.pgmId) {
          return item.pgmId;
        }

        // 2. 탭 경로와 일치하는지 확인
        for (const menuItem of submenu.items || []) {
          if (menuItem.tabs) {
            const matchingTab = menuItem.tabs.find(tab => tab.path === currentPath);
            if (matchingTab && menuItem.pgmId) {
              return menuItem.pgmId;
            }
          }
        }
      }
    }
  }

  return null;
});

// iframe 모드일 때 현재 메뉴의 타이틀 찾기
const currentIframeTitle = computed(() => {
  const currentPath = route.path;

  for (const section of menuSections.value) {
    // Direct items 확인
    if (section.items) {
      const item = section.items.find(item => item.path === currentPath);
      if (item) {
        return item.name || item.pgmNm;
      }
    }

    // Submenus 확인
    if (section.submenus) {
      for (const submenu of section.submenus) {
        if (submenu.items) {
          const item = submenu.items.find(item => item.path === currentPath);
          if (item) {
            return item.name || item.pgmNm;
          }

          // 탭이 있는 경우 탭 경로도 확인
          for (const menuItem of submenu.items) {
            if (menuItem.tabs) {
              const tab = menuItem.tabs.find(t => t.path === currentPath);
              if (tab) {
                return menuItem.name || menuItem.pgmNm;
              }
            }
          }
        }
      }
    }
  }

  return null;
});

// pgmId가 변경될 때만 iframe 키 업데이트 (탭 변경 시에는 리마운트 방지)
watch(
  currentPgmId,
  (newPgmId, oldPgmId) => {
    if (newPgmId !== oldPgmId) {
      console.log('🔄 pgmId 변경 → iframeKey 증가:', { oldPgmId, newPgmId });
      iframeKey.value++;
    }
  },
);

// 오버플로우를 숨길 페이지 목록
const noOverflowPages = ['Task-collaboration-board'];

// 오버플로우를 숨길지 여부 계산
const shouldHideOverflow = computed(() => noOverflowPages.includes(route.name));

// 사이드바 확장 상태 관리
const isExpanded = ref(false);
const hasRestoredPinnedState = ref(false);

const getSidebarPinnedStorageKey = (compId, userId) => `sgate.sidebarPinned.${compId}.${userId}`;
const sidebarPinnedStorageKey = computed(() => {
  const compId = authStore.user?.compId;
  const userId = authStore.user?.userId;
  if (!compId || !userId) return null;
  return getSidebarPinnedStorageKey(compId, userId);
});

const restoreSidebarPinnedState = key => {
  if (!key || !process.client) return null;
  let stored = null;
  try {
    stored = localStorage.getItem(key);
    if (stored === null) {
      stored = sessionStorage.getItem(key);
    }
  } catch (error) {
    stored = null;
  }
  if (stored === 'true' || stored === 'false') {
    return stored === 'true';
  }
  return null;
};

// 페이지 전환 애니메이션 상태
const isTransitioning = ref(false);
const mainContent = ref(null);

// SAI 모달 상태 (store에서 직접 가져오기)
const saiChatStore = useSaiChatStore();
const { isSaiModalOpen } = storeToRefs(saiChatStore);

// 세션 만료 스토어
const sessionExpiredStore = useSessionExpiredStore();
const { isVisible: isSessionExpiredVisible } = storeToRefs(sessionExpiredStore);
const handleSessionExpiredConfirm = () => sessionExpiredStore.handleConfirm();

// 자식 컴포넌트에서 접근할 수 있도록 provide
provide('isExpanded', isExpanded);
provide('shouldRenderAsis', shouldRenderAsis);
provide('currentIframeTitle', currentIframeTitle);
provide('metaToken', metaToken); // 일반 페이지에서도 meta 사용 가능

// 사이드바 확장 상태 토글 함수
const toggleExpanded = () => {
  console.log('toggleExpanded 이후 값:', isExpanded.value);
};
// WebSocket 스토어
// 세션 슬라이딩 연장(SockJS heartbeat) 원인 확인을 위해 상시 연결 임시 비활성화
// const webSocketStore = useWebSocketStore();

// 컴포넌트 마운트 시 확장 상태 복원
onMounted(() => {
  // 애니메이션 초기화
  AOS.init({
    once: true,
    duration: 800,
  });

  // 로그인 페이지가 아닌 경우에만 WebSocket·다른 탭 로그아웃 수신 연결
  if (!isLoginPage.value) {
    // webSocketStore.initialize(); // SockJS 상시 연결 → 세션 만료 연장 가능하여 임시 주석
    startSessionMonitor();
    // 세션 유휴 타임아웃 → 자동 로그아웃 (AS-IS layout.jsp 세션 타이머 이식)
    startSessionTimeout();
  }
});

watch(
  sidebarPinnedStorageKey,
  key => {
    if (hasRestoredPinnedState.value || !key) return;
    const restored = restoreSidebarPinnedState(key);
    if (restored !== null) {
      isExpanded.value = restored;
    }
    hasRestoredPinnedState.value = true;
  },
  { immediate: true },
);

// 컴포넌트 언마운트 시 WebSocket 정리
onUnmounted(() => {
  // webSocketStore.cleanup(); // initialize 주석에 맞춰 함께 비활성화
  stopSessionMonitor();
  stopSessionTimeout();
});

watch(isLoginPage, isLogin => {
  if (isLogin) {
    stopSessionMonitor();
    stopSessionTimeout();
  } else {
    startSessionMonitor();
    startSessionTimeout();
  }
});

// 라우터 변경 시 페이지 전환 애니메이션 실행
watch(
  () => route.path,
  async () => {
    isTransitioning.value = true;

    await nextTick();

    // 애니메이션 완료 후 클래스 제거
    setTimeout(() => {
      isTransitioning.value = false;
    }, 300);

    // 페이지 변경 시 meta 초기화 (새로운 페이지에서 meta가 없으면)
    if (!route.query.meta) {
      metaToken.value = null;
    }
  },
);

// meta 쿼리 파라미터 감지
watch(
  () => route.query.meta,
  newMeta => {
    if (newMeta) {
      // meta 토큰 저장
      metaToken.value = newMeta;

      // URL에서 meta 파라미터 제거
      const query = { ...route.query };
      delete query.meta;
      router.replace({ path: route.path, query });
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.app-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-right 0.3s ease-in-out; /* SAI 모달 열림/닫힘 애니메이션 */

  &.sai-open {
    margin-right: 350px; /* SAI 모달 너비만큼 오른쪽 마진 추가하여 왼쪽으로 밀림 */
  }
}

.content-wrapper {
  display: flex;
  flex: 1;
  width: 100%;
}

/* HR 평가 설정 페이지: overflow 제거 → 창 스크롤로 좌/우 sticky 동작, min-width 완화 */
.main-content.sticky-scroll {
  overflow: visible;
  overflow-x: visible;
  height: auto;
  align-self: flex-start;
  min-width: 900px;
}

.main-content {
  padding-left: 95px; /* 사이드바 기본 너비 */
  padding-right: 16px;
  padding-top: 76px; /* 헤더 아래 여백 */
  box-sizing: border-box; /* 패딩이 너비에 포함되도록 설정 */
  width: 100%;
  min-width: 1200px;
  overflow-y: auto;
  overflow-x: auto;

  &.expanded {
    padding-left: 290px; /* 핀 고정 시 사이드바 너비(80px) + 서브메뉴 너비(200px) */
  }

  &.asis-mode {
    padding-left: 80px !important;
    padding-right: 0 !important;
    padding-top: 56px !important;
    padding-bottom: 0 !important;
    overflow: hidden !important; /* main 스크롤 제거 */
    height: 100vh !important; /* main 높이 고정 */

    &.expanded {
      padding-left: 280px !important; /* 확장된 사이드바 너비 조정 */
      padding-right: 0 !important;
      padding-top: 56px !important;
      padding-bottom: 0 !important;
      overflow: hidden !important; /* main 스크롤 제거 */
      height: 100vh !important; /* main 높이 고정 */
    }
  }

  &.overflow-hidden {
    overflow: hidden;
  }

  &.page-transition {
    animation: fade-in 0.3s ease-out;
  }

  &.login-page {
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-width: unset !important;
  }

  &.no-sidebar {
    padding-left: 16px !important;
  }

  /* KPI 실적 관리: 기본 min-width(1200px)보다 넓게 — scoped 단일 클래스로 특이도 확보 */
  &.kpi-main-min-wide {
    min-width: 1400px;
  }

  /* 보드는 카테고리 목록 안에서만 가로 이동한다. */
  &.task-board-scroll {
    min-width: 0;
    overflow-x: hidden;
  }
}

@keyframes fade-in {
  from {
    transform: translateX(-12px);
  }
  to {
    transform: translateX(0);
  }
}
</style>
